import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { visuallyHidden } from "@mui/utils";
import api from "../../api";
import SnackBarComponent from "../../components/SnackBarComponent"; // Check path based on folder structure
import { useDebounce } from "../../hooks/useDebounce";

// --- HELPER FUNCTIONS ---
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
    { id: "tag_title", numeric: false, disablePadding: true, label: "Tag Title" },
    { id: "tag_description", numeric: false, disablePadding: false, label: "Description" },
    { id: "created_at", numeric: false, disablePadding: false, label: "Created At" },
    { id: "actions", numeric: false, disablePadding: false, label: "Actions" },
];

function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                    />
                </TableCell>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? "right" : "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

function EnhancedTableToolbar(props) {
    const { numSelected, handleDelete, handleAdd, searchQuery, setSearchQuery } = props;

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                }),
            }}
        >
            {numSelected > 0 ? (
                <Typography sx={{ flex: "1 1 100%" }} color="inherit" variant="subtitle1" component="div">
                    {numSelected} selected
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Typography sx={{ flexShrink: 0 }} variant="h6" id="tableTitle" component="div">
                        TAG MANAGEMENT
                    </Typography>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search Tags..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ ml: 'auto', width: 300 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            )}

            {numSelected > 0 ? (
                <Tooltip title="Delete">
                    <IconButton onClick={handleDelete}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Create Tag">
                    <IconButton onClick={handleAdd}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Toolbar>
    );
}

export default function Tags() {
    const [rows, setRows] = useState([]);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("tag_title");
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [dense, setDense] = useState(true);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(true);

    // Search
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

    // Dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Form Data
    const [formData, setFormData] = useState({
        tag_title: "",
        tag_description: ""
    });

    // Notifications
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [errorMessage, setErrorMessage] = useState(null);

    // --- API CALLS ---
    const fetchTags = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("accessToken");
        try {
            // Adjust URL based on your routing
            const response = await api.get("/api/tags", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRows(response.data.data || []);
            setIsLoading(false);
        } catch (error) {
            console.error("Fetch error:", error);
            setErrorMessage(error.response?.data?.message || "Error fetching tags");
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTags();
    }, []);

    const handleSave = async () => {
        if (!formData.tag_title || !formData.tag_description) {
            setSnackbar({ open: true, message: "Please fill all fields", severity: "warning" });
            return;
        }

        const token = localStorage.getItem("accessToken");
        const apiUrl = "/api/tags";

        try {
            if (isEditing) {
                // Update
                await api.put(apiUrl, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSnackbar({ open: true, message: "Tag updated successfully!", severity: "success" });
            } else {
                // Create
                await api.post(apiUrl, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSnackbar({ open: true, message: "Tag created successfully!", severity: "success" });
            }
            setOpenDialog(false);
            fetchTags();
            setFormData({ tag_title: "", tag_description: "" });
        } catch (error) {
            const msg = error.response?.data?.error || error.response?.data?.message || "Operation failed";
            setSnackbar({ open: true, message: msg, severity: "error" });
        }
    };

    const handleDeleteConfirm = async () => {
        const token = localStorage.getItem("accessToken");
        try {
            await Promise.all(selected.map(id =>
                api.delete("/api/tags", {
                    headers: { Authorization: `Bearer ${token}` },
                    data: { tag_id: id } // Body required for delete based on your controller
                })
            ));

            setSnackbar({ open: true, message: "Tags deleted successfully", severity: "success" });
            setSelected([]);
            setOpenDeleteDialog(false);
            fetchTags();
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to delete tags", severity: "error" });
        }
    };

    // --- HANDLERS ---
    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = filteredRows.map((n) => n.tag_id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        setSelected(newSelected);
    };

    const handleEditClick = (row) => {
        setFormData({
            tag_id: row.tag_id,
            tag_title: row.tag_title,
            tag_description: row.tag_description
        });
        setIsEditing(true);
        setOpenDialog(true);
    };

    const handleAddClick = () => {
        setFormData({ tag_title: "", tag_description: "" });
        setIsEditing(false);
        setOpenDialog(true);
    };

    // --- FILTERING ---
    const filteredRows = useMemo(() => {
        if (!debouncedSearchQuery) return rows;
        const lowerQuery = debouncedSearchQuery.toLowerCase();
        return rows.filter(row =>
            row.tag_title.toLowerCase().includes(lowerQuery) ||
            row.tag_description.toLowerCase().includes(lowerQuery)
        );
    }, [rows, debouncedSearchQuery]);

    useEffect(() => { setPage(0); }, [debouncedSearchQuery]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;
    const visibleRows = useMemo(() =>
        [...filteredRows].sort(getComparator(order, orderBy))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [order, orderBy, page, rowsPerPage, filteredRows]
    );

    return (
        <Box sx={{ width: "100%" }}>
            {snackbar.open && (
                <SnackBarComponent
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                />
            )}

            <Paper sx={{ width: "100%", mb: 2 }}>
                <EnhancedTableToolbar
                    numSelected={selected.length}
                    handleAdd={handleAddClick}
                    handleDelete={() => setOpenDeleteDialog(true)}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                />
                <TableContainer>
                    <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={filteredRows.length}
                        />
                        <TableBody>
                            {isLoading ? (
                                <TableRow><TableCell colSpan={4} align="center"><CircularProgress /></TableCell></TableRow>
                            ) : visibleRows.length === 0 ? (
                                <TableRow><TableCell colSpan={4} align="center"><Alert severity="info">No tags found.</Alert></TableCell></TableRow>
                            ) : (
                                visibleRows.map((row, index) => {
                                    const isItemSelected = isSelected(row.tag_id);
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow
                                            hover
                                            onClick={(event) => handleClick(event, row.tag_id)}
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.tag_id}
                                            selected={isItemSelected}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox color="primary" checked={isItemSelected} inputProps={{ "aria-labelledby": labelId }} />
                                            </TableCell>
                                            <TableCell component="th" id={labelId} scope="row" padding="none">
                                                {row.tag_title}
                                            </TableCell>
                                            <TableCell>{row.tag_description}</TableCell>
                                            <TableCell>{new Date(row.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit">
                                                    <IconButton onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* ADD / EDIT DIALOG */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditing ? "Edit Tag" : "Create New Tag"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Tag Title"
                            value={formData.tag_title}
                            onChange={(e) => setFormData({ ...formData, tag_title: e.target.value })}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Description"
                            value={formData.tag_description}
                            onChange={(e) => setFormData({ ...formData, tag_description: e.target.value })}
                            fullWidth
                            multiline
                            rows={3}
                            required
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {isEditing ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DELETE DIALOG */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete {selected.length} selected tag(s)?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}